import React from 'react';

const TermsOfService = ({ onBack, embedded }) => {
  const s = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f0f4f8',
      color: '#1a2e4a',
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", sans-serif',
    },
    wrapper: { maxWidth: '860px', margin: '0 auto' },
    header: { marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '1px solid #c8d8e8' },
    backLink: {
      display: 'inline-block', marginBottom: '1.5rem', color: '#0EA5E9',
      textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
    },
    title: { fontSize: '2.2rem', fontWeight: 700, margin: '0 0 0.4rem', color: '#0A1B3A' },
    subtitle: { fontSize: '0.9rem', color: '#5a7a9a', margin: 0 },
    sectionTitle: {
      fontSize: '1.1rem', fontWeight: 700, color: '#0EA5E9',
      marginBottom: '0.75rem', marginTop: '2rem', textTransform: 'uppercase', letterSpacing: '0.04em',
    },
    p: { marginBottom: '0.9rem', color: '#2a4060', fontSize: '0.93rem', lineHeight: 1.75 },
    li: { marginLeft: '1.5rem', marginBottom: '0.6rem', color: '#2a4060', fontSize: '0.93rem', lineHeight: 1.7 },
    em: { color: '#0EA5E9', fontWeight: 600 },
    box: {
      backgroundColor: '#e6f2fb', border: '1px solid #b3d4eb', borderRadius: 10,
      padding: '1.25rem 1.5rem', margin: '1rem 0', fontSize: '0.93rem',
    },
    warnBox: {
      backgroundColor: '#fff8e6', border: '1px solid #f0c040', borderRadius: 10,
      padding: '1.25rem 1.5rem', margin: '1rem 0', fontSize: '0.93rem', color: '#5a4010',
    },
    footer: {
      marginTop: '3rem', paddingTop: '1.5rem', borderTop: '1px solid #c8d8e8',
      fontSize: '0.82rem', color: '#7a99b8', textAlign: 'center',
    },
  };

  const inner = (
    <div style={embedded ? {} : s.wrapper}>
      {!embedded && (
        <div style={s.header}>
          <span style={s.backLink} onClick={onBack || (() => window.history.back())}>← Back</span>
          <h1 style={s.title}>Terms of Service</h1>
          <p style={s.subtitle}>Effective: April 2026 &nbsp;·&nbsp; Mancini Business Consulting LLC</p>
        </div>
      )}
      {embedded && (
        <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #c8d8e8' }}>
          <h2 style={s.title}>Terms of Service</h2>
          <p style={s.subtitle}>Effective: April 2026 &nbsp;·&nbsp; Mancini Business Consulting LLC</p>
        </div>
      )}

        <p style={s.p}>
          These Terms of Service ("Terms") govern your use of the Vella platform operated by
          <strong> Mancini Business Consulting LLC</strong> ("Company," "we," "us"), a North Carolina
          limited liability company. By accessing or using Vella, you agree to these Terms. If you
          do not agree, do not use the Service.
        </p>

        <h2 style={s.sectionTitle}>1. Acceptable Use</h2>
        <p style={s.p}>You may use Vella only for lawful marketing purposes. You agree <strong>not</strong> to:</p>
        <ul>
          <li style={s.li}>Send unsolicited SMS messages ("spam") to recipients who have not provided explicit prior written consent.</li>
          <li style={s.li}>Violate the Telephone Consumer Protection Act (TCPA), CAN-SPAM Act, GDPR, CCPA, or any other applicable law.</li>
          <li style={s.li}>Impersonate any person, brand, or entity, or make false or misleading statements.</li>
          <li style={s.li}>Send messages containing threats, harassment, hate speech, illegal content, or adult material without age verification.</li>
          <li style={s.li}>Attempt unauthorized access to our systems, other users' accounts, or connected APIs.</li>
          <li style={s.li}>Use automated scraping, bots, or scripts to extract data or circumvent rate limits.</li>
          <li style={s.li}>Reverse-engineer the platform, AI models, or proprietary algorithms.</li>
          <li style={s.li}>Resell access to the Service without our written consent.</li>
        </ul>
        <p style={s.p}>Violations may result in immediate account suspension and potential legal action.</p>

        <h2 style={s.sectionTitle}>2. AI Disclaimer</h2>
        <div style={s.warnBox}>
          <strong>AI-generated content is provided "as is."</strong> Vella's AI features (chat, copy generation, campaign recommendations, voice responses) are powered by large language models that can produce inaccurate, incomplete, or misleading output. Mancini Business Consulting LLC is <strong>not responsible for errors, omissions, or decisions made based on AI-generated content.</strong> You are solely responsible for reviewing, editing, and approving any content before publishing or sending it. Do not rely on AI output as legal, financial, medical, or professional advice.
        </div>

        <h2 style={s.sectionTitle}>3. Subscription &amp; Billing</h2>
        <p style={s.p}>Vella operates on monthly or annual subscription plans billed via Stripe. By subscribing, you authorize recurring charges to your payment method. Plans auto-renew unless cancelled before the renewal date.</p>

        <h2 style={s.sectionTitle}>4. Refund Policy</h2>
        <div style={s.box}>
          <strong>30-Day Refund Guarantee:</strong> If you are not satisfied within the first 30 days of a new subscription, contact us at <span style={s.em}>team@amplifx.com</span> for a full refund of unused subscription fees. Refund requests must be submitted within 30 days of the original charge date. Charges for SMS messages already sent, overages, or additional one-time purchases are non-refundable.
        </div>
        <p style={s.p}>After the 30-day window, no partial refunds are issued for unused portions of a billing period.</p>

        <h2 style={s.sectionTitle}>5. Cancellation</h2>
        <p style={s.p}>
          You may cancel your subscription at any time from your account settings or by emailing us. Cancellation takes effect at the end of your current billing period — you retain full access to Vella until that date. We do not charge cancellation fees.
        </p>

        <h2 style={s.sectionTitle}>6. Service Availability</h2>
        <p style={s.p}>
          We strive for high availability but <strong>make no SLA (Service Level Agreement) guarantees.</strong> The Service is provided on a best-effort basis. We may perform scheduled or unscheduled maintenance that results in temporary downtime. We will make reasonable efforts to notify you of planned maintenance. Mancini Business Consulting LLC is not liable for losses caused by Service interruptions.
        </p>

        <h2 style={s.sectionTitle}>7. SMS Compliance</h2>
        <p style={s.p}>You are solely responsible for:</p>
        <ul>
          <li style={s.li}>Obtaining explicit prior written consent from every SMS recipient before sending messages through Vella.</li>
          <li style={s.li}>Honoring STOP opt-out requests promptly (within the legally required timeframe).</li>
          <li style={s.li}>Including sender identification and callback numbers in all messages.</li>
          <li style={s.li}>Maintaining consent records for a minimum of 24 months.</li>
          <li style={s.li}>Complying with all applicable carrier guidelines and TCPA requirements.</li>
        </ul>
        <div style={s.warnBox}>
          TCPA violations can result in fines of $500–$1,500 per message. You assume full legal and financial responsibility for your SMS campaigns.
        </div>

        <h2 style={s.sectionTitle}>8. Intellectual Property</h2>
        <p style={s.p}>The Vella platform, branding, and underlying technology are owned by Mancini Business Consulting LLC. You retain ownership of content you create. By using the platform, you grant us a limited, non-exclusive license to process your content for the sole purpose of delivering the Service.</p>

        <h2 style={s.sectionTitle}>9. Limitation of Liability</h2>
        <p style={s.p}>
          TO THE MAXIMUM EXTENT PERMITTED BY NORTH CAROLINA LAW, MANCINI BUSINESS CONSULTING LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, DATA LOSS, OR BUSINESS INTERRUPTION. OUR TOTAL LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE AMOUNT YOU PAID IN THE 3 MONTHS PRECEDING THE CLAIM.
        </p>

        <h2 style={s.sectionTitle}>10. Indemnification</h2>
        <p style={s.p}>You agree to indemnify and hold harmless Mancini Business Consulting LLC from any claims, damages, fines, or costs (including attorneys' fees) arising from your use of the Service, violation of these Terms, or violation of any law — including TCPA violations from SMS campaigns you send.</p>

        <h2 style={s.sectionTitle}>11. Termination</h2>
        <p style={s.p}>We may suspend or terminate your account immediately if you violate these Terms, fail to pay, or engage in illegal activity. Upon termination, your right to access the Service ends immediately. We will retain your data for up to 90 days before deletion (see Privacy Policy).</p>

        <h2 style={s.sectionTitle}>12. Governing Law &amp; Jurisdiction</h2>
        <p style={s.p}>
          These Terms are governed by the laws of the <strong>State of North Carolina</strong>, without regard to conflict-of-law principles. Any dispute must be brought in the state or federal courts located in <strong>Wake County, North Carolina</strong>. Both parties consent to personal jurisdiction in those courts.
        </p>

        <h2 style={s.sectionTitle}>13. Changes to These Terms</h2>
        <p style={s.p}>We may update these Terms from time to time. We will notify you by email or in-app notice at least 7 days before material changes take effect. Continued use after the effective date constitutes acceptance.</p>

        <h2 style={s.sectionTitle}>14. Contact</h2>
        <div style={s.box}>
          <strong>Mancini Business Consulting LLC</strong><br />
          Raleigh, NC<br />
          Email: <span style={s.em}>team@amplifx.com</span>
        </div>

        <div style={s.footer}>
          © 2026 Mancini Business Consulting LLC. All rights reserved.
        </div>
    </div>
  );

  return embedded ? inner : <div style={s.container}>{inner}</div>;
};

export default TermsOfService;
